import type { SupabaseClient } from "@supabase/supabase-js";
import type { Dayjs } from "@punchup/utils/dayjs";
import { logException } from "@punchup/sentry";
import type { Database } from "../../supabase.types";
import type { VenueShowReminder } from "../../models/show-reminders";
import {
  NAIVE_TIMESTAMP,
  REMINDER_PAGE_SIZE,
  WIDEST_VENUE_UTC_OFFSET_HOURS,
  toShowReminderCandidate,
} from "./fetch-reminder-candidates";

const HISTORY_DAYS = 30;

const VENUE_REMINDER_COLUMNS = `
  id,
  reminder_before_show_value,
  reminder_before_show_unit,
  reminder_sent_at,
  send_comedian_reminder,
  comedian_shows!inner(
    id,
    title,
    datetime,
    venue_id,
    show_comedian_pairings(comedian_id)
  ),
  booking_contact_responses!inner(
    id,
    status,
    show_id,
    comedian_id,
    contact_name,
    contact_email,
    performance_time,
    performance_duration,
    lineup_position,
    comedians(display_name),
    booking_contact_invitations!inner(
      booking_contacts!inner(name, email, phone, is_active),
      bookings!inner(venue_page_id, venue_pages!inner(id, name, slug, timezones))
    )
  )
`;

/**
 * Every reminder setting on a venue's shows that are not deleted, from `HISTORY_DAYS` before `now` onward (widened by the widest venue UTC offset, as show times are venue wall-clock time), whatever
 * the booking's status and whether the reminder is switched on: the cron switches a reminder off when it disables it,
 * so the page needs switched-off rows to say why.
 *
 * Called with the service role, it bypasses RLS: the caller must already have checked that the viewer administers
 * `venuePageId`.
 */
export async function fetchVenueShowReminders(
  supabase: SupabaseClient<Database>,
  { venuePageId, now }: { venuePageId: string; now: Dayjs }
): Promise<VenueShowReminder[]> {
  const historyStart = now
    .utc()
    .subtract(HISTORY_DAYS, "day")
    .subtract(WIDEST_VENUE_UTC_OFFSET_HOURS, "hour")
    .format(NAIVE_TIMESTAMP);

  const reminders: VenueShowReminder[] = [];
  let lastSettingId: string | null = null;

  for (;;) {
    const forVenue = supabase
      .from("performance_notification_settings")
      .select(VENUE_REMINDER_COLUMNS)
      .eq(
        "booking_contact_responses.booking_contact_invitations.bookings.venue_page_id",
        venuePageId
      )
      .is("comedian_shows.deleted_at", null)
      .gte("comedian_shows.datetime", historyStart);
    const { data, error } = await (
      lastSettingId === null ? forVenue : forVenue.gt("id", lastSettingId)
    )
      .order("id")
      .limit(REMINDER_PAGE_SIZE);

    if (error) {
      logException(error, {
        data: "[show-reminders] Failed to fetch a venue's reminders",
        extra: { venuePageId },
      });
      throw error;
    }

    for (const row of data ?? []) {
      reminders.push({
        ...toShowReminderCandidate(row),
        sentAt: row.reminder_sent_at,
        switchedOn: row.send_comedian_reminder,
        contactActive:
          row.booking_contact_responses.booking_contact_invitations
            .booking_contacts.is_active,
      });
    }

    if (!data || data.length < REMINDER_PAGE_SIZE) return reminders;
    lastSettingId = data[data.length - 1].id;
  }
}
